import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Package, MapPin, IndianRupee } from "lucide-react";
import { useNavigate } from "react-router-dom";

const COLORS = ["#0A192F", "#002FA7", "#3B82F6", "#6366F1", "#8B5CF6", "#A855F7"];

const ReportsPage = () => {
  const { API, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [servicesReport, setServicesReport] = useState([]);
  const [locationsReport, setLocationsReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [servicesRes, locationsRes] = await Promise.all([
        axios.get(`${API}/reports/services`, getAuthHeader()),
        axios.get(`${API}/reports/locations`, getAuthHeader()),
      ]);
      setServicesReport(servicesRes.data);
      setLocationsReport(locationsRes.data);
    } catch (error) {
      console.error("Fetch reports error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Prepare data for charts
  const serviceChartData = servicesReport.slice(0, 8).map((item) => ({
    name: item._id?.name || "Unknown",
    revenue: item.revenue || 0,
    bookings: item.bookings || 0,
  }));

  const categoryData = servicesReport.reduce((acc, item) => {
    const cat = item._id?.category || "other";
    acc[cat] = (acc[cat] || 0) + (item.revenue || 0);
    return acc;
  }, {});

  const pieData = Object.entries(categoryData).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-500">Loading reports...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-950" data-testid="reports-title">
          Reports & Analytics
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Service usage, revenue, and location-wise analytics
        </p>
      </div>

      <Tabs defaultValue="services" className="space-y-4">
        <TabsList className="bg-zinc-100">
          <TabsTrigger value="services" data-testid="tab-services-report">
            <Package className="w-4 h-4 mr-2" />
            Services
          </TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations-report">
            <MapPin className="w-4 h-4 mr-2" />
            Locations
          </TabsTrigger>
        </TabsList>

        {/* Services Report */}
        <TabsContent value="services" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Revenue by Service */}
            <Card className="border-zinc-200 shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
                  Revenue by Service
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  {serviceChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={serviceChartData} layout="vertical" margin={{ left: 80 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                        <XAxis type="number" tickFormatter={(v) => `₹${v / 1000}k`} tick={{ fontSize: 11 }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="revenue" fill="#0A192F" radius={[0, 2, 2, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-zinc-400">
                      No data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Revenue by Category */}
            <Card className="border-zinc-200 shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
                  Revenue by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-zinc-400">
                      No data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Services Table */}
          <Card className="border-zinc-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
                Service Performance Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50/50">
                    <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Service</TableHead>
                    <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Category</TableHead>
                    <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Type</TableHead>
                    <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Bookings</TableHead>
                    <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {servicesReport.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                        No service data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    servicesReport.map((item, idx) => (
                      <TableRow key={idx} className="hover:bg-zinc-50">
                        <TableCell className="font-medium text-zinc-900">{item._id?.name || "Unknown"}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs capitalize">
                            {item._id?.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-zinc-600 capitalize">{item._id?.type}</TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-700">{item.bookings}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">{formatCurrency(item.revenue)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Locations Report */}
        <TabsContent value="locations" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Scans by Location */}
            <Card className="border-zinc-200 shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
                  QR Scans by Location
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50/50">
                      <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Location</TableHead>
                      <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider text-right">Scans</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {locationsReport?.scans_by_location?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center py-8 text-zinc-500">
                          No scan data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      locationsReport?.scans_by_location?.slice(0, 10).map((item, idx) => (
                        <TableRow key={idx} className="hover:bg-zinc-50">
                          <TableCell>
                            <div>
                              <p className="font-medium text-zinc-900">{item._id?.location}</p>
                              <p className="text-xs text-zinc-500">
                                {item._id?.terminal} | {item._id?.zone}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge className="bg-violet-100 text-violet-700">{item.scans}</Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Bookings by Zone */}
            <Card className="border-zinc-200 shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
                  Bookings by Zone
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50/50">
                      <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Zone</TableHead>
                      <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Bookings</TableHead>
                      <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider text-right">Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {locationsReport?.bookings_by_zone?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-zinc-500">
                          No booking data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      locationsReport?.bookings_by_zone?.map((item, idx) => (
                        <TableRow key={idx} className="hover:bg-zinc-50">
                          <TableCell>
                            <div>
                              <p className="font-medium text-zinc-900 capitalize">{item._id?.zone}</p>
                              <p className="text-xs text-zinc-500">{item._id?.terminal}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="secondary" 
                              className="cursor-pointer hover:bg-zinc-300 transition-colors"
                              onClick={() => navigate("/bookings")}
                            >
                              {item.bookings} bookings
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(item.revenue)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
